import _ from 'lodash';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { IRadioButtonGroupProps, RadioButtonGroup } from '@cb/apricot-react';

import { RadioButton } from 'src/components';
import { FORM_ERROR_MAPPING } from 'src/constants';
import { TCommonFieldProps, TDropItem } from 'src/types';

import Typography from './Typography';

interface IRadioFieldProps extends IRadioButtonGroupProps, TCommonFieldProps {
  comment?: string;
  name: string;
  options: TDropItem[];
}

const RadioField: FC<IRadioFieldProps> = ({
  comment,
  label,
  name,
  readOnly,
  required,
  options,
  showManualError,
  validator,
}) => {
  const {
    formState: { errors },
    clearErrors,
    register,
    setValue,
    unregister,
    watch,
  } = useFormContext();

  const value = watch(name) || '';
  const errorType = _.get(errors, `${name}.type`) as unknown as string;
  const formValues = watch();
  let errorMessage = errorType ? FORM_ERROR_MAPPING[errorType] : undefined;

  if (showManualError && validator?.validate && !validator?.validate(formValues)) {
    errorMessage = errorMessage ?? validator?.validationMessage ?? FORM_ERROR_MAPPING.invalid;
  }

  useEffect(() => {
    register(name, { required });
    setValue(name, value);

    return () => {
      unregister(name);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister, name, required]);

  const handleChange = (value: string) => {
    if (readOnly) {
      return;
    }

    setValue(name, value);
    clearErrors(name);
  };

  return (
    <>
      {/* @ts-ignore */}
      <RadioButtonGroup
        hSpacing="32"
        legend={label}
        name={name}
        required={required}
        onChange={handleChange}
        value={value}
        validation={errorMessage ? 'error' : undefined}
        validationMsg={errorMessage}>
        {options.map((option) => (
          <RadioButton key={option.value} label={option.label} value={option.value} />
        ))}
      </RadioButtonGroup>
      {comment ? <Typography variant="body1">{comment}</Typography> : null}
    </>
  );
};

export default RadioField;
